<template>
  <main class="screen">
    <Card>
      <div class="wrap">
        <template v-if="phase === 'loading'">
          <Spinner />
          <p class="text">{{ loadingText }}</p>
        </template>

        <template v-else-if="phase === 'done'">
          <div class="check">✓</div>
          <h2 class="title">{{ doneTitle }}</h2>
          <p class="text">{{ doneHint }}</p>
        </template>

        <template v-else>
          <div class="icon">!</div>
          <h2 class="title">處理失敗</h2>
          <p class="text">{{ errorMsg }}</p>
        </template>
      </div>

      <template v-if="phase !== 'loading'" #footer>
        <button class="btn btn-primary full" @click="onConfirm">確認</button>
      </template>
    </Card>
  </main>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import Card from '@/components/Card.vue'
import Spinner from '@/components/Spinner.vue'
import { cancelPayment, confirmPayment } from '@/api/qrSession'
import { loadSessionId, saveSessionId } from '@/utils/storage'
import { friendlyError } from '@/utils/errorMessage'

const props = defineProps<{
  mode: 'confirm' | 'cancel'
}>()

const route = useRoute()

const posMachineCode = computed(() => String(route.params.posMachineCode || ''))
const loadingText = computed(() =>
  props.mode === 'confirm' ? '正在確認付款…' : '正在取消付款…',
)
const doneTitle = computed(() =>
  props.mode === 'confirm' ? '已成功付款' : '已取消付款',
)
const doneHint = computed(() => '請按下「確認」關閉此分頁,回到原分頁繼續。')

const phase = ref<'loading' | 'done' | 'error'>('loading')
const errorMsg = ref('')

/** 這頁是另開的新分頁,按確認就直接關掉,原分頁靠 polling 顯示後續狀態 */
function onConfirm() {
  window.close()
}

/** 先取 URL query `?sid=xxx`(另開分頁 sessionStorage 空),沒有再退回同分頁的 sessionStorage */
function resolveSessionId(): string | null {
  const q = route.query.sid
  if (typeof q === 'string' && q) return q
  return loadSessionId(posMachineCode.value)
}

onMounted(async () => {
  if (!posMachineCode.value) {
    errorMsg.value = '缺少販賣機編號'
    phase.value = 'error'
    return
  }
  const sessionId = resolveSessionId()
  if (!sessionId) {
    errorMsg.value = '找不到交易資訊,請回到原分頁重試'
    phase.value = 'error'
    return
  }
  try {
    const vo = props.mode === 'confirm'
      ? await confirmPayment(posMachineCode.value, sessionId)
      : await cancelPayment(posMachineCode.value, sessionId)
    /* 若因為 LINE App 導回 = 開新分頁的情境,原分頁的 sessionStorage 讀不到,
       這裡用 API 回來的 QrSessionVo.sessionId 補寫回目前分頁的 sessionStorage */
    saveSessionId(posMachineCode.value, vo.sessionId)
    phase.value = 'done'
  } catch (e) {
    errorMsg.value = friendlyError(e, '通路回跳處理失敗')
    phase.value = 'error'
  }
})
</script>

<style scoped>
.screen {
  min-height: 100vh;
  background: linear-gradient(180deg, #fff7ed 0%, #f9fafb 220px);
  padding-bottom: env(safe-area-inset-bottom, 16px);
}
.wrap {
  text-align: center;
  padding: 20px 0;
}
.check {
  width: 56px;
  height: 56px;
  margin: 0 auto 12px;
  background: #10b981;
  color: #fff;
  border-radius: 50%;
  font-size: 32px;
  line-height: 56px;
  font-weight: 700;
}
.icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 12px;
  background: #dc2626;
  color: #fff;
  border-radius: 50%;
  font-size: 30px;
  line-height: 56px;
  font-weight: 700;
}
.title {
  margin: 0;
  font-size: 20px;
  color: #111827;
}
.text {
  margin: 10px 0 0;
  color: #4b5563;
  font-size: 14px;
  line-height: 1.6;
}
.full {
  width: 100%;
}
</style>
